import { FlatTreeControl } from '@angular/cdk/tree';
import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { each, every, isEmpty, reduce, some, toLower } from 'lodash';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { PropertyType } from '@shared/enums/property-type.enum';
import { PropertyBasicInfo, PropertyBasicInfoNode } from '@shared/interfaces/propertyBasicInfo';
import { ISimpleChanges } from '@shared/types/simple-changes-typed.type';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { NODES_RE_INIT_DELAY } from './functions/tree-utils';

type SelectedNodeMap = Record<string, boolean>;
type FlatTreeNode = {
  name: string;
  level: number;
  expandable: boolean;
  node: PropertyBasicInfoNode;
};

@Component({
  selector: 'app-select-tree',
  templateUrl: './select-tree.component.html',
  styleUrls: ['./select-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe],
})
export class SelectTreeComponent extends ComponentAbstract implements OnInit, OnChanges, OnDestroy {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private decimalPipe: DecimalPipe = inject(DecimalPipe);
  private snackbarService: SnackbarService = inject(SnackbarService);

  @Input() containerCss = 'display-flex flex-column';
  @Input() label = '';
  @Input() insideLabel = '';
  @Input() labelCss = 'body-small-bold nowrap';
  @Input() nodes: PropertyBasicInfoNode[] = [];
  @Input() declare isSuperUser: boolean;
  @Input() selectedNodes: PropertyBasicInfoNode[];
  @Input() declare warningMessage: string;
  @Input() labelRequired = false;
  @Input() displaySkeleton = false;
  @Input() leafTypes: PropertyType[] = [PropertyType.Unit];
  @Input() emptyPlaceholder = 'Select property';
  @Input() selectionLabel = 'units';
  @Input() selectAllByDefault = false;
  @Input() selectAllEvent = new EventEmitter();
  @Output() selectedRoots = new EventEmitter<PropertyBasicInfoNode[]>();
  @Output() clearAll = new EventEmitter();

  filterString = '';
  placeholder = 'Select property';
  mappedSelectedNodeIds: Set<string> = new Set();
  selectedNodeIds: SelectedNodeMap = {};
  visibleNodeIds = new Set<number>();

  private treeFlattener = new MatTreeFlattener<PropertyBasicInfoNode, FlatTreeNode>(
    (node: PropertyBasicInfoNode, level: number) => ({
      name: node.name,
      level,
      expandable: !!node.children?.length,
      node,
    }),
    node => node.level,
    node => node.expandable,
    node => node.children || []
  );
  treeControl = new FlatTreeControl<FlatTreeNode>(
    node => node.level,
    node => node.expandable
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  readonly EColorPalette = EColorPalette;
  readonly EIcon = EIcon;

  constructor() {
    super(inject(ChangeDetectorRef));
    this.updatePlaceholderAndEmit = this.updatePlaceholderAndEmit.bind(this);
  }

  hasChild = (_: number, node: FlatTreeNode) => node.expandable;

  ngOnInit(): void {
    this.placeholder = this.emptyPlaceholder;
  }

  ngOnChanges(changes: ISimpleChanges<SelectTreeComponent>) {
    if (changes.nodes?.currentValue) {
      this.nodes = changes.nodes.currentValue ?? [];
      if (this.nodes?.[0]?.children) {
        this.nodes[0].children = this.nodes[0].children.sort((a, b) => a.name.localeCompare(b.name));
      }
      this.linkParents(this.nodes);
      this.dataSource.data = this.nodes ?? [];
      this.setVisibleNodes('');
      this.expandRoot();
      this.treeInitialized();
    }

    if (changes['isSuperUser']?.currentValue) {
      setTimeout(() => {
        this.selectedNodes = this.nodes;
        this.treeInitialized();
      });
    }
    if (changes['selectedNodes']?.currentValue === null) {
      if (this.selectAllByDefault && this.nodes?.length) {
        setTimeout(() => {
          this.selectedNodes = this.nodes;
          this.treeInitialized();
        });
      }
    }
  }

  treeInitialized() {
    this.selectNodes(this.selectedNodes || []);
  }

  clearSelection() {
    this.selectedNodeIds = {};
    this.updatePlaceholderAndEmit(this.selectedNodeIds);
    this.clearAll.emit();
    this.checkIsSuperUser();
  }

  hasNodesSelected(): boolean {
    return some(Object.values(this.selectedNodeIds), value => value);
  }

  selectNodes(propertiesToSelect: PropertyBasicInfo[]) {
    const propertyIdsToSelect = new Set(propertiesToSelect.map(property => property.id));
    const selectedLeafNodeIds = { ...this.selectedNodeIds };
    const traverseAndCollect = (node: PropertyBasicInfoNode) => {
      if (propertyIdsToSelect.has(node.id)) {
        toggleNodeSelection(node, true, selectedLeafNodeIds);
        selectedLeafNodeIds[node.id] = true;
      }
      if (node.children) {
        node.children.forEach(traverseAndCollect);
      }
    };
    this.nodes?.forEach(traverseAndCollect);
    this.selectedNodeIds = selectedLeafNodeIds;
    this.updatePlaceholderAndEmit(selectedLeafNodeIds);
  }

  filterTree(event: Event) {
    const text = (event.target as HTMLInputElement).value;
    this.setVisibleNodes(text);

    if (isEmpty(text)) {
      this.collapseAll();
      this.expandRoot();
      return;
    }
  }

  expandRoot() {
    const rootNode = this.treeControl.dataNodes?.find(node => node.level === 0);
    if (rootNode) {
      this.treeControl.expand(rootNode);
    }
  }

  getAncestorsName(node: PropertyBasicInfoNode) {
    if (node.parent) {
      return this.getAncestorsName(node.parent) + node.name;
    }

    return node.name;
  }

  toggleMenu(menuExpanded: boolean) {
    this.isExpanded = menuExpanded;

    if (this.isExpanded) {
      this.onListOpen();
    } else {
      this.onListClose();
    }

    this.cdr.detectChanges();
  }

  isNodeSelected(node: PropertyBasicInfoNode): boolean {
    return isNodeSelected(node, this.selectedNodeIds);
  }

  isNodeIndeterminate(node: PropertyBasicInfoNode): boolean {
    if (!node.children || node.children.length === 0) {
      return false;
    }

    const hasSelectedChild = node.children.some(child => this.isNodeSelected(child) || this.isNodeIndeterminate(child));
    const hasUnselectedChild = node.children.some(child => !this.isNodeSelected(child));
    return hasSelectedChild && hasUnselectedChild;
  }

  onNodeSelectionChange(node: PropertyBasicInfoNode, isSelected: boolean) {
    const selectedLeafNodeIds = { ...this.selectedNodeIds };
    toggleNodeSelection(node, isSelected, selectedLeafNodeIds);
    selectedLeafNodeIds[node.id] = isSelected;

    this.selectedNodeIds = selectedLeafNodeIds;
    this.updatePlaceholderAndEmit(selectedLeafNodeIds);
    this.checkIsSuperUser();
  }

  updatePlaceholderAndEmit(selectedNodeIds: SelectedNodeMap) {
    const { selectedLeaves, selectedRoots, isRootSelected } = this.getSelectedNodes(selectedNodeIds);
    const effectiveSelectedLeaves =
      selectedLeaves.length > 0
        ? selectedLeaves
        : this.nodes?.[0]
        ? this.getSelectedLeavesRecursive([], this.nodes[0], false)
        : [];
    this.selectedRoots.emit(selectedRoots);
    const allCount = this.decimalPipe.transform(effectiveSelectedLeaves.length, '1.0-0');
    if (!isEmpty(effectiveSelectedLeaves)) {
      let insideLabel = this.insideLabel?.length > 15 ? this.insideLabel.slice(0, 15) + '...' : this.insideLabel;
      insideLabel = insideLabel ? `${insideLabel} - ` : '';
      const label = this.selectionLabel || 'items';
      const singularLabel = label.endsWith('ies')
        ? `${label.slice(0, -3)}y`
        : label.endsWith('s')
        ? label.slice(0, -1)
        : label;
      this.placeholder = isRootSelected
        ? `${insideLabel}All ${allCount} ${label}`
        : effectiveSelectedLeaves?.length === 1
        ? `${insideLabel ? insideLabel : 'Selected: '}${effectiveSelectedLeaves.length} ${singularLabel}`
        : `${insideLabel ? insideLabel : 'Selected: '}${effectiveSelectedLeaves.length} ${label}`;
    } else {
      this.placeholder = this.emptyPlaceholder;
    }
  }

  collapseAll() {
    this.treeControl.collapseAll();
  }

  isNodeVisible(node: PropertyBasicInfoNode): boolean {
    return this.visibleNodeIds.has(node.id);
  }

  getSelectedNodes(selectedNodeIds: SelectedNodeMap) {
    const entries = Object.entries(selectedNodeIds);
    let isRootSelected = false;

    const onlySelectedNodes = reduce(
      entries,
      (acc, [id, isChecked]) => {
        if (isChecked) {
          if (id === '-1') {
            isRootSelected = true;
          }
          acc.push(id);
        }
        return acc;
      },
      []
    );

    this.mappedSelectedNodeIds = new Set(onlySelectedNodes);

    const isSingleRoot = this.nodes?.length === 1;
    if (isSingleRoot || !this.nodes?.[0]) {
      const rootNode = this.nodes?.[0];
      const selectedRoots = rootNode ? this.getSelectedRootsRecursive([], rootNode) : [];
      const selectedLeaves = rootNode ? this.getSelectedLeavesRecursive([], rootNode) : [];

      return { selectedRoots, selectedLeaves, isRootSelected };
    }

    const rootNodes = this.nodes;
    const selectedRoots = rootNodes.reduce((acc, node) => acc.concat(this.getSelectedRootsRecursive([], node)), []);
    const selectedLeaves = rootNodes.reduce((acc, node) => acc.concat(this.getSelectedLeavesRecursive([], node)), []);

    return { selectedRoots, selectedLeaves, isRootSelected };
  }

  private getSelectedLeavesRecursive(acc, node: PropertyBasicInfoNode, useLeafTypes = true) {
    const isLeaf = !node.children || node.children.length === 0;
    const matchesLeafType = !useLeafTypes || this.leafTypes?.length === 0 || this.leafTypes.includes(node.propertyType);
    if (isLeaf && this.mappedSelectedNodeIds.has(node.id.toString()) && matchesLeafType) {
      acc.push(node);
      return acc;
    }

    if (node.children) {
      each(node.children, childNode => this.getSelectedLeavesRecursive(acc, childNode, useLeafTypes));
    }
    return acc;
  }

  private getSelectedRootsRecursive(acc, node: PropertyBasicInfoNode) {
    if (node.parent && this.mappedSelectedNodeIds.has(node.id.toString())) {
      acc.push(node);
      return acc;
    }

    if (node.children) {
      each(node.children, childNode => this.getSelectedRootsRecursive(acc, childNode));
    }
    return acc;
  }

  private setVisibleNodes(filterText: string) {
    this.visibleNodeIds.clear();
    const normalized = toLower(filterText || '');

    if (!normalized) {
      this.nodes?.forEach(node => this.markAllVisible(node));
      return;
    }

    this.nodes?.forEach(node => this.markVisibleByFilter(node, normalized));
  }

  private markVisibleByFilter(node: PropertyBasicInfoNode, filterText: string): boolean {
    const matches = toLower(this.getAncestorsName(node)).includes(filterText);
    const childrenMatch = node.children?.some(child => this.markVisibleByFilter(child, filterText)) ?? false;

    if (matches || childrenMatch) {
      this.visibleNodeIds.add(node.id);
      if (childrenMatch) {
        const flatNode = this.treeControl.dataNodes?.find(candidate => candidate.node.id === node.id);
        if (flatNode) {
          this.treeControl.expand(flatNode);
        }
      }
      return true;
    }

    return false;
  }

  private markAllVisible(node: PropertyBasicInfoNode) {
    this.visibleNodeIds.add(node.id);
    node.children?.forEach(child => this.markAllVisible(child));
  }

  private onListOpen() {
    this.filterString = '';
    this.setVisibleNodes('');
    this.collapseAll();
    this.expandRoot();
  }

  private onListClose(): void {
    this.filterString = '';
  }

  checkIsSuperUser(): void {
    if (this.isSuperUser) {
      this.snackbarService.error('Superusers are required to have all properties and portfolios');
      setTimeout(() => {
        this.selectedNodes = this.nodes;
        this.treeInitialized();
      }, NODES_RE_INIT_DELAY);
    }
  }

  private linkParents(nodes: PropertyBasicInfoNode[], parent?: PropertyBasicInfoNode) {
    nodes?.forEach(node => {
      node.parent = parent;
      if (node.children?.length) {
        this.linkParents(node.children, node);
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.snackbarService.dismiss();
  }
}

function toggleNodeSelection(
  node: PropertyBasicInfoNode,
  selectionValue: boolean,
  selectedLeafNodeIds: SelectedNodeMap
) {
  selectedLeafNodeIds[node.id] = selectionValue;

  if (selectionValue) {
    if (node.parent) {
      selectParentIfNeeded(node.parent, selectedLeafNodeIds);
    }
  }

  if (!selectionValue) {
    if (node.parent) {
      deselectAncestorsIfNeeded(node.parent, selectedLeafNodeIds);
    }
  }

  if (!isEmpty(node.children)) {
    node.children.forEach(childNode => toggleNodeSelection(childNode, selectionValue, selectedLeafNodeIds));
  }
}

function isNodeSelected(nodeToCheck: PropertyBasicInfoNode, selectedLeafNodeIds: SelectedNodeMap) {
  if (selectedLeafNodeIds[nodeToCheck.id]) {
    return true;
  }

  if (!nodeToCheck.children || nodeToCheck.children.length === 0) {
    return false;
  }

  return nodeToCheck.children.every(childNode => isNodeSelected(childNode, selectedLeafNodeIds));
}

function selectParentIfNeeded(parentNode: PropertyBasicInfoNode, selectedLeafNodeIds: SelectedNodeMap) {
  if (
    every(parentNode.children, (childNode: PropertyBasicInfoNode) => isNodeSelected(childNode, selectedLeafNodeIds))
  ) {
    selectedLeafNodeIds[parentNode.id] = true;
    if (parentNode.parent) {
      selectParentIfNeeded(parentNode.parent, selectedLeafNodeIds);
    }
  }
}

function deselectAncestorsIfNeeded(parentNode: PropertyBasicInfoNode, selectedLeafNodeIds: SelectedNodeMap) {
  if (
    isNodeSelected(parentNode, selectedLeafNodeIds) &&
    some(parentNode.children, (childNode: PropertyBasicInfoNode) => !isNodeSelected(childNode, selectedLeafNodeIds))
  ) {
    selectedLeafNodeIds[parentNode.id] = false;
    if (parentNode.parent) {
      deselectAncestorsIfNeeded(parentNode.parent, selectedLeafNodeIds);
    }
  }
}
